import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Grid, Paper, CircularProgress, Alert } from '@mui/material'; // Removed Select, InputLabel, FormControl, MenuItem
import ImageCanvas from '../components/annotation/ImageCanvas';
import MetadataEditor from '../components/annotation/MetadataEditor';
import { getNextImageForAnnotation, getAnnotations, saveAnnotations, getImageDetails } from '../services/apiService'; // Added getNextImageForAnnotation
import { pixelToNormalized } from '../utils/coordinateUtils';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';

const AnnotationPage = () => {
  const [currentImageId, setCurrentImageId] = useState(null); // Store the assigned image_id
  const [currentImageDetails, setCurrentImageDetails] = useState(null); // Store { url, size, original_filename, etc.}
  const [annotations, setAnnotations] = useState([]); // Store annotations for the current image { id, type, label, box_2d, tilt_angle }
  const [setInfo, setSetInfo] = useState(null); // Store original set info
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null); // Konva shape ID
  const [imageSize, setImageSize] = useState([0, 0]); // Actual dimensions [width, height]
  const [isLoadingNextImage, setIsLoadingNextImage] = useState(false);
  const [isLoadingAnnotations, setIsLoadingAnnotations] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false); // For the image loading itself
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(''); // General error for the page
  const [imageLoadError, setImageLoadError] = useState(''); // Specific error for image loading
  const [noMoreImages, setNoMoreImages] = useState(false);
  const [showTiltAngles, setShowTiltAngles] = useState(false);


  // Fetch ORIGINAL annotations for a given image ID
  const fetchOriginalAnnotations = useCallback(async (imageId) => {
    // Explicit check again before proceeding, even though fetchNextImage should guard this.
    if (!imageId) {
      console.error("fetchOriginalAnnotations called without a valid imageId.");
      setError("Cannot load annotations: Missing Image ID.");
      return;
    }
    setIsLoadingAnnotations(true);
    setIsLoadingImage(true); // Start image loading indicator along with annotations
    setImageLoadError('');
    setAnnotations([]); // Clear previous annotations
    setSetInfo(null);
    setSelectedAnnotationId(null);

    try {
      const params = { imageId: imageId, setType: 'original' };
      const response = await getAnnotations(params);

      // Process annotations
      const loadedAnnotations = (response.data.annotations || []).map((ann) => ({
        ...ann,
        id: ann.annotation_id, // Use backend ID
        annotationType: ann.annotation_type,
        // Ensure tilt_angle exists for roofs, default if necessary (should be handled by backend ideally)
        tilt_angle: ann.annotation_type === 'roof' ? (ann.tilt_angle ?? 0) : null
      }));
      setAnnotations(loadedAnnotations);
      setSetInfo(response.data.set_info); // Store the original set info

      // If image details weren't fully loaded by getNextImage, fetch them now
      // Use optional chaining safely
      if (!currentImageDetails?.fullUrl && imageId) {
        try {
          const detailsResp = await getImageDetails(imageId);
          setCurrentImageDetails(detailsResp.data);
          if (detailsResp.data?.size) setImageSize(detailsResp.data.size); // Safety check for size
        } catch (detailsErr) {
          console.error("Error fetching image details after annotation load:", detailsErr);
          // Avoid overwriting existing details if fetch fails
          setImageLoadError(prevError => prevError || `Failed to load image details for ${imageId}.`);
        }
      }

    } catch (err) {
      console.error(`Error fetching original annotations for image ${imageId}:`, err); // Corrected comma position
      const errMsg = err.response?.data?.error || `Failed to load annotations for image ${imageId}.`;
      // Display error, but allow potential image display if details loaded
      setError(prev => prev ? `${prev}\n${errMsg}` : errMsg);
      setImageLoadError(errMsg); // Set specific image load error
      toast.error(errMsg);
      setAnnotations([]);
    } finally {
      setIsLoadingAnnotations(false);
      // isLoadingImage will be turned off by the effect watching imageSize/currentImageDetails
    }
  }, [currentImageDetails]); // Depend on currentImageDetails to potentially trigger detail fetch

  // Fetch the next image to annotate
  const fetchNextImage = useCallback(async () => {
    setIsLoadingNextImage(true);
    setIsLoadingAnnotations(false); // Reset annotation loading status
    setIsLoadingImage(false);      // Reset image loading status
    setError('');
    setImageLoadError('');
    setNoMoreImages(false);
    setCurrentImageId(null);
    setCurrentImageDetails(null);
    setAnnotations([]);
    setSetInfo(null);
    setSelectedAnnotationId(null);
    setImageSize([0, 0]);
    setShowTiltAngles(false); // Reset tilt visibility for new image

    try {
      const response = await getNextImageForAnnotation();
      // The service now constructs fullUrl and size
      const receivedImageId = response.data?.image_id; // Use optional chaining
      if (receivedImageId) {
        setCurrentImageId(receivedImageId);
        setCurrentImageDetails(response.data); // Contains url, size, filename etc.
        if (response.data.size) {
          setImageSize(response.data.size);
          setImageLoadError(''); // Clear previous load error if new image details are valid
        } else {
          // If size is missing from the response, explicitly try fetching details
          console.warn("getNextImage response missing size, attempting detail fetch...");
          try {
            const detailsResp = await getImageDetails(receivedImageId);
            // Update details and size from the secondary fetch
            setCurrentImageDetails(detailsResp.data);
            if (detailsResp.data?.size) {
              setImageSize(detailsResp.data.size);
            } else {
              throw new Error("Image details also missing size information.");
            }
          } catch (detailsErr) {
            console.error("Error fetching image details after getNextImage:", detailsErr);
            setImageLoadError(`Failed to load image dimensions for ${receivedImageId}. Cannot annotate.`);
            setCurrentImageId(null); // Prevent annotation attempts without size
            return; // Stop further processing for this image
          }
        }
        // Now fetch the ORIGINAL annotations for this image
        await fetchOriginalAnnotations(receivedImageId); // Pass the confirmed ID
      } else {
        // This case should ideally not happen if backend sends 404 correctly, but handle just in case
        // Check if it's a valid 'no more images' response without an ID
        if (response?.status === 200 && !receivedImageId && response?.data?.message?.includes('No more images')) {
          setNoMoreImages(true);
          setCurrentImageId(null);
        } else {
          setError('Received invalid data when fetching next image.');
          toast.error('Received invalid data when fetching next image.');
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNoMoreImages(true); // Specific state for "no more images"
        setCurrentImageId(null); // Ensure ID is cleared
        setError(''); // Clear general error, specific message is shown
      } else {
        console.error("Error fetching next image:", err);
        const errMsg = err.response?.data?.error || 'Failed to load next image. Please try again later.';
        setError(errMsg);
        toast.error(errMsg);
      }
    } finally {
      setIsLoadingNextImage(false);
    }
  }, [fetchOriginalAnnotations]); // Dependency on fetchOriginalAnnotations


  // Fetch the first image on component mount
  useEffect(() => {
    fetchNextImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchNextImage is stable due to useCallback with [] dependency, so it's okay to disable here

  // Effect to manage the visual image loading state based on details and size
  useEffect(() => {
    // If we have details but no size, or annotations are loading, consider image loading
    if ((currentImageDetails?.fullUrl && imageSize[0] === 0) || isLoadingAnnotations) {
      setIsLoadingImage(true);
    }
    // If size is set OR there's an image load error, visual loading is done/failed
    if ((imageSize[0] > 0 && imageSize[1] > 0) || imageLoadError) {
      setIsLoadingImage(false);
    }
    // If no details, not loading
    if (!currentImageDetails) {
      setIsLoadingImage(false);
    }
  }, [currentImageDetails, imageSize, isLoadingAnnotations, imageLoadError]);


  // Add a new bounding box
  const addNewBox = (type) => {
    if (!currentImageId || imageSize[0] === 0 || imageSize[1] === 0) {
      toast.warn('Please wait for an image to load with valid dimensions.');
      return;
    }
    const defaultPixelBox = { x: 50, y: 50, width: 100, height: 100 }; // Adjusted default position
    const normalizedBox = pixelToNormalized(defaultPixelBox, imageSize);
    const newAnnotation = {
      id: `${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      annotationType: type,
      label: '',
      box_2d: normalizedBox,
      tilt_angle: type === 'roof' ? 0 : null // Initialize tilt to 0 for new roofs
    };
    setAnnotations(prev => [...prev, newAnnotation]);
    setSelectedAnnotationId(newAnnotation.id);
  };

  // Save the current annotations
  const handleSave = async () => {
    // *** ADDED EXPLICIT CHECK ***
    if (!currentImageId) {
      toast.error("Cannot save: No image is currently loaded or assigned.");
      return;
    }
    // Check if image dimensions are valid before saving
    if (!imageSize || imageSize[0] === 0 || imageSize[1] === 0) {
      toast.error("Cannot save: Image dimensions are missing or invalid.");
      return;
    }

    // Validate box format before sending
    let hasInvalidBox = false;
    annotations.forEach((ann, index) => {
      if (!ann.box_2d || !Array.isArray(ann.box_2d) || ann.box_2d.length !== 4 || ann.box_2d.some(coord => typeof coord !== 'number' || !isFinite(coord))) {
        console.error(`Invalid box_2d found at index ${index}:`, ann);
        hasInvalidBox = true;
      }
      // Optional: Check if box coordinates are within the 0-1000 range expected by backend
      if (ann.box_2d && ann.box_2d.some(coord => coord < 0 || coord > 1000)) {
        console.warn(`Warning: box_2d coordinates outside [0, 1000] range at index ${index}:`, ann.box_2d);
        // Allow saving but log warning, or set hasInvalidBox = true to block
      }
      // Validate tilt angle format
      if (ann.annotationType === 'roof' && ann.tilt_angle !== null && (typeof ann.tilt_angle !== 'number' || !isFinite(ann.tilt_angle))) {
        console.error(`Invalid tilt_angle found at index ${index}:`, ann);
        hasInvalidBox = true;
      }
    });


    if (hasInvalidBox) {
      toast.error("Cannot save: Some annotations have invalid data (check console). Please correct them.");
      return;
    }

    setIsSaving(true);
    setError(''); // Clear previous errors on save attempt
    try {
      const annotationsToSend = annotations.map(({ id, ...rest }) => ({
        ...rest,
        label: rest.label || '',
        // Ensure tilt_angle is number or null, handle potential strings if needed (though UI should prevent)
        tilt_angle: rest.annotationType === 'roof' && rest.tilt_angle !== null ? parseFloat(rest.tilt_angle) : null,
        // Ensure box_2d contains integers as expected by backend (if schema requires integers)
        box_2d: rest.box_2d.map(coord => Math.round(coord))
      }));

      console.log("Sending annotations:", JSON.stringify(annotationsToSend, null, 2)); // Log before sending

      const response = await saveAnnotations(currentImageId, annotationsToSend);
      toast.success(response.data.message || 'Annotations saved successfully!');
      // Fetch the next image automatically after successful save
      fetchNextImage(); // fetchNextImage handles resetting state
    } catch (err) {
      console.error("Error saving annotations:", err);
      // Log the detailed error response if available
      if (err.response) {
        console.error("Save Error Response Data:", err.response.data);
        console.error("Save Error Response Status:", err.response.status);
        console.error("Save Error Response Headers:", err.response.headers);
      }
      const errMsg = err.response?.data?.error || err.message || 'Failed to save annotations.';
      toast.error(`Save Failed: ${errMsg}`);
      setError(`Save Failed: ${errMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Skip current image and fetch next one
  const handleSkip = () => {
    toast.info("Skipping current image...");
    fetchNextImage(); // fetchNextImage handles resetting state
  }

  const handleToggleTilt = () => {
    setShowTiltAngles(prev => !prev);
  };

  // Determine if controls should be disabled
  const controlsDisabled = !currentImageId || isSaving || isLoadingImage || isLoadingAnnotations || !!imageLoadError || imageSize[0] === 0 || noMoreImages;


  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>Image Annotation</Typography>

      {/* Display general page errors */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading Indicator for fetching the next image */}
      {isLoadingNextImage && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <LoadingSpinner />
          <Typography>Loading next image...</Typography>
        </Paper>
      )}

      {/* Message when no more images are available */}
      {noMoreImages && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="info">No more images available for you to annotate at this time. Check back later!</Alert>
        </Paper>
      )}

      {/* Main Annotation Area - Show only if not loading next, not out of images, and have an ID */}
      {!isLoadingNextImage && !noMoreImages && currentImageId && (
        <Grid container spacing={2}>
          {/* Left Side: Canvas and Controls */}
          <Grid item xs={12} md={7} lg={8}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {currentImageDetails?.original_filename && (
                <Typography variant="h6" gutterBottom>
                  Annotating: {currentImageDetails.original_filename}
                  {setInfo && <Typography variant="caption" sx={{ ml: 1 }}>(Using Original Set ID: {setInfo.set_id})</Typography>}
                </Typography>
              )}

              {/* Canvas Container with Loading/Error Handling */}
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, position: 'relative' /* Needed for absolute loader */ }}>
                {/* Combined Loading Indicator for Image/Annotations */}
                {(isLoadingImage || isLoadingAnnotations) && !imageLoadError && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                    <LoadingSpinner />
                    <Typography sx={{ ml: 1 }}>
                      {isLoadingImage && !imageSize[0] ? 'Loading Image Data...' : isLoadingAnnotations ? 'Loading Annotations...' : 'Loading...'}
                    </Typography>
                  </Box>
                )}

                {/* Display Image Load Error if it exists and we are not actively loading annotations */}
                {imageLoadError && !isLoadingAnnotations && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <Alert severity="error" sx={{ width: '100%' }}>{imageLoadError}</Alert>
                  </Box>
                )}

                {/* Canvas Area - Render only when ready */}
                {
                  !isLoadingImage &&                   // Image itself is not loading
                  !isLoadingAnnotations &&            // Annotations are not loading
                  currentImageDetails?.fullUrl &&     // We have a URL
                  !imageLoadError &&                 // No specific image loading error
                  imageSize[0] > 0 && imageSize[1] > 0 && // Image dimensions are known
                  (
                    <ImageCanvas
                      key={currentImageDetails.fullUrl} // Re-mount on URL change
                      imageUrl={currentImageDetails.fullUrl}
                      annotations={annotations}
                      setAnnotations={setAnnotations}
                      selectedId={selectedAnnotationId}
                      setSelectedId={setSelectedAnnotationId}
                      imageSize={imageSize}
                      setImageSize={setImageSize}
                      isLoadingImage={false} // Already handled above
                      imageLoadError={null} // Already handled above
                      showTiltAngles={showTiltAngles} // Pass state down
                    />
                  )
                }
              </Box>

              {/* Buttons below canvas */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' /* Align buttons left */ }}>
                <Button variant="contained" onClick={() => addNewBox('roof')} disabled={controlsDisabled}>Add Roof Field</Button>
                <Button variant="contained" color="secondary" onClick={() => addNewBox('obstruction')} disabled={controlsDisabled}>Add Obstruction</Button>
                <Button variant="contained" color="success" onClick={handleSave} disabled={controlsDisabled}>
                  {isSaving ? <CircularProgress size={24} /> : 'Save & Next Image'}
                </Button>
                <Button variant="outlined" color="warning" onClick={handleSkip} disabled={isSaving || isLoadingNextImage || !currentImageId /* Disable skip if no image loaded */}>
                  Skip Image
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Right Side: Editor */}
          <Grid item xs={12} md={5} lg={4}>
            {/* Ensure the Paper takes full height available in the grid item */}
            <Paper sx={{ p: 3, height: '100%' }}>
              <MetadataEditor
                annotations={annotations}
                setAnnotations={setAnnotations}
                selectedId={selectedAnnotationId}
                setSelectedId={setSelectedAnnotationId}
              />
              <Button variant="outlined" onClick={handleToggleTilt} disabled={controlsDisabled}>
                {showTiltAngles ? 'Hide Tilt Angles' : 'Show Tilt Angles'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
      {/* Display message only if not loading, no image ID, AND not in the "no more images" state, AND no error */}
      {!isLoadingNextImage && !currentImageId && !noMoreImages && !error && (
        <Typography sx={{ textAlign: 'center', mt: 5 }}>Please wait, assigning image...</Typography>
      )}

    </Box>
  );
};

export default AnnotationPage;